import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';

import { JobDetailFormState } from '../../types';

type FieldDef = VonqContractPostingRequirements['posting_requirements'][number];
type Autocomplete = FieldDef['autocomplete'];

/**
 * A single resolved source for a dependent facet's `term` parameter.
 *   - `facet`   → read the current value of a sibling posting-requirement facet
 *                 (VONQ Pattern 3 — dependent facet).
 *   - `vacancy` → read a field from the job/opening itself, e.g. the title
 *                 (VONQ Pattern 4 — vacancy field).
 */
export type TermSource =
  | { kind: 'facet'; key: string }
  | { kind: 'vacancy'; key: string };

/**
 * The runtime shape of a single `parameters_source.term` entry. The generated
 * OpenAPI model types `parameters_source.term` as a single object
 * `{ field?; facet? }`, but per the VONQ docs the API also returns an *array* of
 * these objects for multi-dependency facets. Generated files must not be
 * hand-edited (the real fix is upstream in the monolith Swagger spec), so we
 * narrow the true shape locally.
 */
type RawTermEntry = { field?: string | null; facet?: string | null };

/**
 * Maps a VONQ "vacancy field" name (from `parameters_source.term.field`) to the
 * corresponding key in our job-details form state. VONQ only uses `title` in
 * practice; unknown fields are intentionally omitted so the dependent facet
 * stays disabled rather than sending a meaningless term.
 */
export const VACANCY_FIELD_TO_FORM_KEY: Record<
  string,
  keyof JobDetailFormState
> = {
  title: 'title',
};

/**
 * Parses a facet's autocomplete config into an ordered list of term sources.
 * Order is preserved so that a multi-dependency `term` array can be built in the
 * exact order VONQ expects. Returns `[]` when the facet has no
 * `parameters_source.term` (i.e. it is not a dependent/vacancy facet).
 */
export function parseTermSources(autocomplete: Autocomplete): TermSource[] {
  const rawTerm = autocomplete?.parameters_source?.term as
    | RawTermEntry
    | RawTermEntry[]
    | null
    | undefined;
  if (!rawTerm) return [];
  const entries = Array.isArray(rawTerm) ? rawTerm : [rawTerm];
  return entries
    .map((entry): TermSource | null => {
      if (entry.facet) return { kind: 'facet', key: entry.facet };
      if (entry.field) return { kind: 'vacancy', key: entry.field };
      return null;
    })
    .filter((source): source is TermSource => source !== null);
}

type ContractValues = Record<string, string | string[] | null | undefined>;
type VacancyValues = Partial<Record<keyof JobDetailFormState, unknown>>;

/**
 * Reduces a facet/vacancy value to a single scalar term string. MULTIPLE parent
 * facets store an array — VONQ expects a single key, so we take the first entry.
 */
function toScalarTerm(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length > 0 ? String(value[0] ?? '') : '';
  }
  return value == null ? '' : String(value);
}

/**
 * Builds the `term` payload for a dependent facet's autocomplete request:
 *   - single source  → a plain string
 *   - multiple sources → an ordered `string[]` matching `parameters_source.term`
 *
 * Returns `null` when *any* source is unresolved (empty value, or an unknown
 * vacancy field), signalling that the fetch must be blocked and the input kept
 * disabled until all parents are filled.
 */
export function resolveTermValue(
  sources: TermSource[],
  contractValues: ContractValues,
  vacancyValues: VacancyValues,
): string | string[] | null {
  if (sources.length === 0) return null;

  const values: string[] = [];
  for (const source of sources) {
    let raw: string;
    if (source.kind === 'facet') {
      raw = toScalarTerm(contractValues[source.key]);
    } else {
      const formKey = VACANCY_FIELD_TO_FORM_KEY[source.key];
      if (!formKey) return null; // unknown vacancy field — cannot resolve
      raw = toScalarTerm(vacancyValues[formKey]);
    }
    const trimmed = raw.trim();
    if (!trimmed) return null; // any unresolved source blocks the fetch
    values.push(trimmed);
  }

  return values.length === 1 ? values[0] : values;
}
